<template>
  <div class="container-fluid mt-3">
    <h2>Global Companies</h2>

    <div class="row mb-3 gy-3">
      <div class="col-md-5">
        <label for="searchQuery" class="form-label">Search Companies</label>
        <input
          id="searchQuery"
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="Search by name, business ID, VAT, or EECS account..."
        />
      </div>
      <div class="col-md-4">
        <label for="countryFilter" class="form-label">Country</label>
        <select
          id="countryFilter"
          v-model="selectedCountryCode"
          class="form-select"
        >
          <option value="">All Countries</option>
          <option
            v-for="country in SUPPORTED_COUNTRIES_WITH_LABELS"
            :key="country.value"
            :value="country.value"
          >
            {{ country.label }} ({{ country.value }})
          </option>
        </select>
      </div>
      <div class="col-md-3">
        <label for="limit" class="form-label">Limit</label>
        <input
          id="limit"
          v-model.number="limit"
          type="number"
          class="form-control"
          min="1"
        />
      </div>
    </div>

    <div v-if="loading" class="alert alert-info">Loading companies...</div>
    <div v-if="!loading && !hasError" class="table-responsive">
      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Business ID</th>
            <th>VAT</th>
            <th>Address</th>
            <th>Country</th>
            <th>EECS Account</th>
            <th>Devices (Count)</th>
            <th>Device Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="companies.length === 0">
            <td colspan="9" class="text-center">No companies found.</td>
          </tr>
          <tr v-for="company in companies" :key="company.id">
            <td>{{ company.id }}</td>
            <td>{{ company.name }}</td>
            <td>{{ company.business_id }}</td>
            <td>{{ company.vat || "-" }}</td>
            <td>{{ company.address || "-" }}</td>
            <td>{{ company.country }}</td>
            <td>{{ company.account_eecs || "-" }}</td>
            <td>{{ company.devices.length }}</td>
            <td>
              <button
                v-if="company.devices.length > 0"
                class="btn btn-sm btn-outline-primary"
                @click="showDeviceDetails(company)"
              >
                View Devices
              </button>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal for Device Details -->
    <div
      v-if="selectedCompany"
      class="modal fade show"
      tabindex="-1"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
      @click.self="closeDeviceModal"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Device Details for {{ selectedCompany.name }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeDeviceModal"
            ></button>
          </div>
          <div class="modal-body">
            <div
              v-if="selectedCompany.devices.length === 0"
              class="text-center"
            >
              No devices associated with this company.
            </div>
            <table v-else class="table table-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Capacity (MW)</th>
                  <th>Technology</th>
                  <th>Comm. Date</th>
                  <th>Country</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="device in selectedCompany.devices" :key="device.id">
                  <td>{{ device.id }}</td>
                  <td>{{ device.name }}</td>
                  <td>{{ device.code }}</td>
                  <td>{{ device.code_type }}</td>
                  <td>{{ device.capacity ?? "-" }}</td>
                  <td>{{ device.technology_code || "-" }}</td>
                  <td>{{ device.commercial_operation_date || "-" }}</td>
                  <td>{{ device.country }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeDeviceModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watchDebounced } from "@vueuse/core";
import { onMounted, ref, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi"; // Import goSolidApi
import { SUPPORTED_COUNTRIES_WITH_LABELS } from "@/constants"; // Import countries

interface Device {
  id: number;
  name: string;
  code: string;
  code_type: string;
  capacity: number | null;
  technology_code: string | null;
  commercial_operation_date: string | null; // Assuming ISO string format
  support: boolean | null;
  country: string;
}

interface Company {
  id: number;
  name: string;
  business_id: string;
  vat: string | null;
  address: string | null;
  country: string;
  account_eecs: string | null;
  devices: Device[];
}

const searchQuery = ref("");
const limit = ref(10000); // Default limit
const companies = ref<Company[]>([]);
const loading = ref(false);
const hasError = ref(false);
const selectedCompany = ref<Company | null>(null);
const selectedCountryCode = ref(""); // Add ref for selected country
const { globalErrorHandler } = useGlobalHandlers();

const fetchGlobalCompanies = async () => {
  loading.value = true;
  hasError.value = false;
  try {
    const params = new URLSearchParams();
    if (searchQuery.value) {
      params.append("search", searchQuery.value);
    }
    if (limit.value > 0) {
      params.append("limit", limit.value.toString());
    }
    if (selectedCountryCode.value) {
      params.append("country_code", selectedCountryCode.value);
    }

    const response = await goSolidApi.get(
      `/api/utils/companies/?${params.toString()}`
      // Headers are automatically added by the goSolidApi interceptor
    );

    // Axios wraps the response data in a `data` property
    companies.value = response.data;
  } catch (err: unknown) {
    companies.value = [];
    hasError.value = true;
    globalErrorHandler(err);
  } finally {
    loading.value = false;
  }
};

const showDeviceDetails = (company: Company) => {
  selectedCompany.value = company;
};

const closeDeviceModal = () => {
  selectedCompany.value = null;
};

// Watch for changes in searchQuery and limit to refetch data
// Debounce search query to avoid excessive API calls
watchDebounced(searchQuery, () => fetchGlobalCompanies(), { debounce: 500 });

watch(limit, () => {
  if (limit.value > 0) {
    fetchGlobalCompanies();
  }
});

watch(selectedCountryCode, () => {
  // Watch for country changes
  fetchGlobalCompanies();
});

// Fetch initial data on component mount
onMounted(() => {
  fetchGlobalCompanies();
});
</script>

<style lang="scss" scoped>
/* Scoped styles for GlobalCompaniesView */
.table-responsive {
  max-height: 70vh; /* Adjust as needed */
}

/* Ensure modal is above other content */
.modal {
  z-index: 1050; /* Default Bootstrap modal z-index */
}
.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
}
</style>
